import { Label, Text } from "@primer/react";
import { MCard } from "../../../components/mobile";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { IMyRegistration } from "../../../model/respone/registration/IMyRegistration";
import { ISubjectGroup } from "../classesHelpers";
import ClassCard from "./ClassCard";

interface ISubjectCardProps {
    subject: ISubjectGroup;
    /** Môn đang được chọn (mở danh sách lớp). */
    selected: boolean;
    onSelect: (subjectKey: string) => void;
    // ----- state đăng ký, truyền xuống ClassCard khi mở -----
    myList: IMyRegistration[];
    pendingClassIds: number[];
    confirmedClassIds: number[];
    gateReason?: string;
    /** Lớp đang mở sub-list LTH/SH. */
    expandedSub: { type: "lth" | "sh"; classId: number } | null;
    onToggleExpand: (type: "lth" | "sh", classId: number) => void;
    onRegister: (classId: number) => void;
    onPickPair: (parentClassId: number, childClassId: number) => void;
}

/**
 * 1 học phần → 1 thẻ accordion (thay cho 1 row bảng listSubject desktop).
 * Chạm thẻ để mở/đóng danh sách lớp tín chỉ của môn đó.
 */
const SubjectCard = ({
    subject,
    selected,
    onSelect,
    myList,
    pendingClassIds,
    confirmedClassIds,
    gateReason,
    expandedSub,
    onToggleExpand,
    onRegister,
    onPickPair,
}: ISubjectCardProps) => {
    // Đọc cờ tài chính từ store — quyết định có hiển thị dòng tiền dưới meta hay không.
    // Cờ on → luôn hiện (kể cả khi sample.so_tien = 0 do thiếu đơn giá — phản ánh đúng trạng thái).
    const showMoney = useAppSelector(s => s.financial.data?.tinh_hoc_phi_theo_so_du === true);

    return (
        <MCard active={selected}>
            <div
                className="m-card-head m-card-head-tap"
                onClick={() => onSelect(subject.subjectKey)}
                role="button"
                aria-expanded={selected}
            >
                <div className="m-card-head-main">
                    <p className="m-card-title">
                        {subject.ky_hieu ? `${subject.ky_hieu} · ` : ""}
                        {subject.ten_mon}
                    </p>
                    <div className="m-meta">
                        <span className="m-meta-item">
                            <i className="fas fa-graduation-cap" />
                            <span className="m-meta-strong">{subject.so_tin_chi} TC</span>
                        </span>
                        {subject.ky_thu != null && (
                            <span className="m-meta-item">
                                <span className="m-meta-label">Kỳ</span>
                                <span className="m-meta-strong">{subject.ky_thu}</span>
                            </span>
                        )}
                        <span className="m-meta-item">
                            <i className="fas fa-layer-group" />
                            {subject.classes.length} lớp
                        </span>
                    </div>
                    {(subject.tinh_chat_lop_text || subject.nhom_tu_chon_text) && (
                        <div className="m-badge-wrap">
                            {subject.tinh_chat_lop_text && (
                                <Label variant="accent">{subject.tinh_chat_lop_text}</Label>
                            )}
                            {subject.nhom_tu_chon_text && (
                                <Label variant="secondary">
                                    Tự chọn: {subject.nhom_tu_chon_text}
                                </Label>
                            )}
                        </div>
                    )}
                    {subject.rang_buoc_text && (
                        <Text
                            sx={{
                                display: "block",
                                fontSize: 0,
                                color: "attention.fg",
                                mt: 1,
                            }}
                        >
                            <i className="fas fa-triangle-exclamation" /> {subject.rang_buoc_text}
                        </Text>
                    )}
                    {/* Tiền học phần — LUÔN hiện khi đợt bật tinh_hoc_phi_theo_so_du, kể cả khi
                        sample.so_tien = 0 (đơn giá thiếu cấu hình — chặn nút Đăng ký ở ClassCard
                        với reason "Chưa có đơn giá", nhưng vẫn show 0đ để SV thấy trạng thái).
                        Lấy từ class đầu tiên có so_tien > 0 (cùng môn = cùng tiền cho cùng SV);
                        fallback classes[0] nếu mọi class đều 0. */}
                    {showMoney && (() => {
                        const sample = subject.classes.find(c => (c.so_tien ?? 0) > 0) ?? subject.classes[0];
                        const tien = sample?.so_tien ?? 0;
                        const giam = sample?.so_tien_mien_giam ?? 0;
                        const phai = sample?.so_tien_phai_nop ?? 0;
                        return (
                            <Text sx={{ display: "block", fontSize: 0, color: "fg.muted", mt: "6px" }}>
                                Số tiền: <b>{tien.toLocaleString("vi-VN")}đ</b>
                                {" · "}Miễn giảm: <b>{giam.toLocaleString("vi-VN")}đ</b>
                                {" · "}Phải nộp: <b>{phai.toLocaleString("vi-VN")}đ</b>
                            </Text>
                        );
                    })()}
                </div>
                <span className={`m-caret ${selected ? "m-caret-open" : ""}`}>
                    <i className="fas fa-chevron-down" />
                </span>
            </div>

            {selected && (
                <div className="m-subwrap">
                    <div className="m-subwrap-title">
                        Lớp tín chỉ được lựa chọn đăng ký
                    </div>
                    {subject.classes.length === 0 ? (
                        <div className="m-tkb-empty">Không có lớp nào.</div>
                    ) : (
                        subject.classes.map(c => (
                            <ClassCard
                                key={c.classId}
                                item={c}
                                myList={myList}
                                pendingClassIds={pendingClassIds}
                                confirmedClassIds={confirmedClassIds}
                                gateReason={gateReason}
                                expanded={expandedSub?.classId === c.classId}
                                onToggleExpand={onToggleExpand}
                                onRegister={onRegister}
                                onPickPair={onPickPair}
                            />
                        ))
                    )}
                </div>
            )}
        </MCard>
    );
};

export default SubjectCard;
